import { Analytics } from '@cobliteam/analytics';
import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { Location } from '@cobliteam/common-types';
import { useCreateRiskRecognitionSilence } from '@cobliteam/hooks/src/useEntity/useRiskRecognitions/useCreateRiskRecognitionSilence';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import useRightPanelStore from '@cobliteam/hooks/src/useRightPanelStore/useRightPanelStore';
import { type TransitionEvent, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import type { NotificationCardRender } from '../../../core/NotificationType';
import useNotificationDrawerStore from '../../../core/store/useNotificationDrawerStore';
import { buildRiskRecognitionEventProps, RISK_RECOGNITION_EVENTS } from '../analytics';
import { getFeedButtonLabelsFromItem, isHighlightRenderable, RISK_DRAWER_PARAM } from '../parse';
import RiskCardBody from '../RiskCardBody';
import useRiskRecognitionSlice from '../store/useRiskSlice';
import useRiskFeedHighlight from './useRiskFeedHighlight';

const RISK_RECOGNITION_TYPE_KEY = 'risk-recognition';

// Fire "Displayed" once per bucket. Reads the slice via getState (not a hook) since it runs inside an effect.
const trackDisplayedOncePerBucket = (highlight: RiskRecognitionHighlight) => {
  const { dismissedBucketEnd, lastDisplayedBucketEnd, markDisplayed } = useRiskRecognitionSlice.getState();
  if (!isHighlightRenderable(highlight, dismissedBucketEnd) || highlight.time_bucket_end === lastDisplayedBucketEnd) return;
  Analytics.track(RISK_RECOGNITION_EVENTS.displayed, buildRiskRecognitionEventProps(highlight, 'card'));
  markDisplayed(highlight.time_bucket_end);
};

/**
 * Controller da surface "card flutuante" do risco. Resolve o
 * highlight atual do feed compartilhado, gateia por flag + página (/monitoring ou seguindo trajeto em
 * /tracking), trata a animação de saída + engajamento + analytics, e devolve o descritor de render pra
 * moldura genérica — ou `null` quando não há nada a mostrar.
 */
const useRiskCardController = (): NotificationCardRender | null => {
  const { knockCardRiskRecognition } = useFeatureFlags();
  const { pathname, search } = useLocation();
  const history = useHistory();
  const { feedClient, feedItems, riskRecognitionFeedItem } = useRiskFeedHighlight();

  const dismissedBucketEnd = useRiskRecognitionSlice((s) => s.dismissedBucketEnd);
  const routeFollowBucketEnd = useRiskRecognitionSlice((s) => s.routeFollowBucketEnd);
  const dismiss = useRiskRecognitionSlice((s) => s.dismiss);
  const openDrawer = useNotificationDrawerStore((s) => s.openDrawer);
  const { mutate: createSilence } = useCreateRiskRecognitionSilence();

  const isRightPanelHidden = useRightPanelStore((s) => s.isRightPanelHidden);

  const highlight = riskRecognitionFeedItem?.highlight;
  const isFollowingOnTracking = pathname === Location.TRACKING && !!routeFollowBucketEnd && highlight?.time_bucket_end === routeFollowBucketEnd;
  const renderAllowed = knockCardRiskRecognition && (pathname === Location.MONITORING || isFollowingOnTracking);
  const canRenderHighlight = renderAllowed && isHighlightRenderable(highlight, dismissedBucketEnd) && !!highlight?.metadata;

  // Keyed by bucket (not a boolean) so the next bucket's fresh highlight never inherits a stale "exiting"
  // flag and auto-dismisses.
  const [exitingBucketEnd, setExitingBucketEnd] = useState<string | null>(null);
  const [exitingItemId, setExitingItemId] = useState<string | null>(null);

  // Fire "Displayed" once per bucket as a renderable Knock event arrives, and mark the feed item as seen
  // because custom Knock surfaces own their engagement calls.
  useEffect(() => {
    if (!(canRenderHighlight && highlight && riskRecognitionFeedItem)) return;

    trackDisplayedOncePerBucket(highlight);
    if (!riskRecognitionFeedItem.item.seen_at) void feedClient.markAsSeen(riskRecognitionFeedItem.item);
  }, [canRenderHighlight, feedClient, highlight, riskRecognitionFeedItem]);

  if (!(canRenderHighlight && riskRecognitionFeedItem && highlight)) return null;

  const isExiting = exitingBucketEnd === highlight.time_bucket_end;
  const { action: actionLabel, dismiss: dismissLabel } = getFeedButtonLabelsFromItem(riskRecognitionFeedItem.item);

  const handleDismiss = () => {
    Analytics.track(RISK_RECOGNITION_EVENTS.dismissed, buildRiskRecognitionEventProps(highlight, 'card'));
    // Fire the personal silence (~2h) in parallel with the exit animation. Guarded: without a detection_id
    // (backend not yet shipping it) we still hide locally.
    const detectionId = highlight.metadata?.detection_id;
    if (detectionId) createSilence({ detectionId, reason: 'dismissed' });
    setExitingItemId(riskRecognitionFeedItem.item.id);
    setExitingBucketEnd(highlight.time_bucket_end);
  };

  const handleExitEnd = (event: TransitionEvent<HTMLDivElement>) => {
    // The opacity leg settles the dismissal; the transform leg fires its own transitionend we ignore.
    // Dismiss the captured exiting bucket, not the live highlight — a new bucket may have arrived mid-animation.
    if (event.propertyName !== 'opacity' || !exitingBucketEnd) return;
    const exitingItem = feedItems.find((item) => item.id === exitingItemId);
    if (exitingItem) void feedClient.markAsArchived(exitingItem);
    dismiss(exitingBucketEnd);
    setExitingBucketEnd(null);
    setExitingItemId(null);
  };

  const handleAction = () => {
    Analytics.track(RISK_RECOGNITION_EVENTS.action, buildRiskRecognitionEventProps(highlight, 'card'));
    void feedClient.markAsInteracted(riskRecognitionFeedItem.item);
    // Abre o drawer pelo store (conteúdo imediato) E reflete na URL (`?risk-recognition-drawer=<id>`) pra
    // virar deep link compartilhável. O host do drawer não re-busca: o store já tem o payload.
    openDrawer(RISK_RECOGNITION_TYPE_KEY, highlight);
    const params = new URLSearchParams(search);
    params.set(RISK_DRAWER_PARAM, riskRecognitionFeedItem.item.id);
    history.push({ search: `?${params.toString()}` });
  };

  return {
    isExiting,
    isRightPanelHidden,
    onTransitionEnd: handleExitEnd,
    body: <RiskCardBody highlight={highlight} actionLabel={actionLabel} dismissLabel={dismissLabel} onDismiss={handleDismiss} onAction={handleAction} />,
  };
};

export default useRiskCardController;
